/**
 * Долги на экране: остаток из записанных платежей, расчёт досрочного,
 * очередь гашения и объяснения словами.
 *
 * Остаток долга меняется от фактов, а не от ручной правки: «Оплатил» по
 * платежу уменьшает его на тело платежа, досрочный перевод — на всю сумму.
 * Правка остатка остаётся для сверки со справкой банка.
 *
 * Объяснения собираются на лету из цифр конкретного долга — шаблонами, без
 * модели. Поэтому они короткие: подставленные числа не должны ломать фразу.
 */
import { ZERO, formatMoney, kop, roundHalfEven, type Money } from './money.js';
import {
  addDays, addMonths, diffDays, isAfter, nextOccurrence, prevOccurrenceInclusive, withDayClamped, ymd, type PlainDate,
} from './date.js';
import type { Loan } from './types.js';

// ─────────────────────────────────────────────────────── остаток из фактов

/** Что сделал банк с досрочным взносом: сократил срок или платёж. */
export type PrepayMode = 'term' | 'payment';

/** Что записано по долгу после даты, на которую назван остаток. */
export interface LoanFact {
  date: PlainDate;
  amount: Money;
  /**
   * `payment` — платёж по графику: сначала набежавшие проценты, остальное в
   * тело. `extra` — досрочный перевод: целиком в тело.
   */
  kind: 'payment' | 'extra';
  /** У досрочного: что сделал банк. Без отметки — сократил срок. */
  mode?: PrepayMode;
  /** Когда записан (ISO): отличает факт дня остатка, сделанный после того, как остаток назван. */
  at?: string;
}

/**
 * Факт после названного остатка: позже его даты — или в тот же день, но
 * записан после того, как остаток назван. Прежде факт дня остатка не
 * считался никогда: долг, заведённый сегодня, не уменьшался от досрочного,
 * внесённого сегодня же.
 */
export const afterNamed = (loan: Pick<Loan, 'principalAsOf' | 'principalSetAt'>, f: LoanFact): boolean =>
  !!loan.principalAsOf && (f.date > loan.principalAsOf
    || (f.date === loan.principalAsOf && !!f.at && !!loan.principalSetAt && f.at >= loan.principalSetAt));

/** Проценты за месяц с остатка: остаток × ставка / 12. */
const monthInterest = (balance: number, ratePct: number): number =>
  roundHalfEven((balance * ratePct) / 1200);

const isLeap = (y: number): boolean => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

/**
 * Проценты за дни с `from` по `to` (не включая `to`) — как считает банк:
 * остаток × ставка × дни / дней в году, в високосный год — 366. В копейках,
 * без округления: округляется сумма, которую забирает платёж.
 */
export function dayInterest(balance: number, ratePct: number, from: PlainDate, to: PlainDate): number {
  if (balance <= 0 || ratePct <= 0 || !isAfter(to, from)) return 0;
  let sum = 0;
  let cur = from;
  while (cur < to) {
    const y = Number(cur.slice(0, 4));
    const next = ymd(y + 1, 1, 1);
    const end = to < next ? to : next;
    sum += (balance * ratePct / 100) * diffDays(cur, end) / (isLeap(y) ? 366 : 365);
    cur = end;
  }
  return sum;
}

/**
 * С какого дня копятся проценты, которых нет в названном остатке.
 *
 * Остаток из справки банка — основной долг. Проценты в нём не видны: они
 * набегают с последнего платежа по графику и уходят следующим платежом.
 * Поэтому отсчёт — с последнего дня платежа не позже даты остатка, но не
 * раньше выдачи.
 */
export function accrualStart(loan: Pick<Loan, 'principalAsOf' | 'paymentDay' | 'openedAt'>): PlainDate | null {
  const asOf = loan.principalAsOf;
  if (!asOf) return null;
  const last = loan.paymentDay >= 1 && loan.paymentDay <= 31 ? prevOccurrenceInclusive(asOf, loan.paymentDay) : asOf;
  return loan.openedAt && isAfter(loan.openedAt, last) && !isAfter(loan.openedAt, asOf) ? loan.openedAt : last;
}

/**
 * Сколько платежей осталось при таком платеже: равный платёж гасит остаток
 * за n месяцев. `null` — платёж не покрывает проценты.
 */
export function annuityLeft(balance: number, payment: number, ratePct: number): number | null {
  if (balance <= 0.5) return 0;
  if (payment <= 0) return null;
  if (ratePct <= 0) return Math.ceil(balance / payment - 1e-9);
  const i = ratePct / 1200;
  if (payment <= balance * i) return null;
  return Math.ceil(Math.log(payment / (payment - balance * i)) / Math.log(1 + i) - 1e-9);
}

/** Равный платёж, гасящий `balance` за `n` месяцев. */
const annuityPayment = (balance: number, ratePct: number, n: number): number => {
  if (n <= 0) return balance;
  if (ratePct <= 0) return balance / n;
  const i = ratePct / 1200;
  return balance * i / (1 - (1 + i) ** -n);
};

/**
 * На сколько уменьшится платёж, когда банк после досрочного сокращает его, а
 * не срок: досрочная сумма «вынимается» из графика на оставшиеся `n` месяцев.
 * Так платёж меняется ровно на её долю и срок остаётся прежним; банк считает
 * так же, а копейки сверяются с его новым графиком.
 */
export function paymentCut(extra: number, ratePct: number, n: number, schedule?: Loan['schedule']): number {
  if (n <= 0) return 0;
  return schedule === 'differentiated' ? extra / n : annuityPayment(extra, ratePct, n);
}

/** Сколько платежей осталось после даты `at`: по сроку договора, а без него — по платежу. */
function paymentsLeft(loan: Loan, balance: number, payment: number, at: PlainDate): number | null {
  if (loan.endDate && loan.paymentDay >= 1) {
    const first = nextOccurrence(at, loan.paymentDay);
    const n = (Number(loan.endDate.slice(0, 4)) - Number(first.slice(0, 4))) * 12
      + Number(loan.endDate.slice(5, 7)) - Number(first.slice(5, 7)) + 1;
    if (n > 0) return n;
  }
  if (loan.schedule === 'differentiated' && loan.termMonths) return loan.termMonths;
  return annuityLeft(balance, payment, loan.ratePct);
}

export interface LoanState {
  /** Основной долг на сегодня. */
  principal: Money;
  /** Проценты, набежавшие с последнего платежа и ещё не оплаченные. */
  accrued: Money;
  /** С какого дня они копятся; `null` — набежавших нет. */
  accruedSince: PlainDate | null;
  /** Платёж по графику: после досрочного с «уменьшить платёж» он меньше записанного. */
  payment: Money;
  /** Сколько процентов добавляется за день при нынешнем остатке. */
  perDay: Money;
}

/**
 * Долг на сегодня — из названного остатка и записанных фактов, с процентами по
 * дням, как у банка.
 *
 * Между фактами проценты копятся на остаток. Платёж по графику сначала
 * забирает набежавшие, остальное уменьшает тело. Досрочный уменьшает тело
 * целиком, и с этого дня проценты идут уже с нового остатка — месяц делится
 * на два отрезка. Если банк при досрочном сократил платёж, а не срок, платёж
 * дальше меньше.
 */
export function loanState(loan: Loan, facts: readonly LoanFact[], today: PlainDate): LoanState {
  const asOf = loan.principalAsOf;
  const start = loan.kind === 'credit_card' ? null : accrualStart(loan);
  const flat = (): LoanState => ({
    principal: loan.principal, accrued: ZERO, accruedSince: null, payment: loan.payment, perDay: ZERO,
  });
  if (!asOf || !start) return flat();
  const rate = loan.ratePct;
  let body: number = loan.principal;
  let payment: number = loan.payment;
  let accrued = 0;
  let since: PlainDate = start;
  let cursor: PlainDate = start;
  const list = facts
    .filter((f) => afterNamed(loan, f) && f.date <= today)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.kind === 'payment' ? -1 : 1));
  for (const f of list) {
    if (body <= 0) break;
    accrued += dayInterest(body, rate, cursor, f.date);
    cursor = f.date;
    if (f.kind === 'payment') {
      const due = roundHalfEven(accrued);
      const toInterest = Math.min(f.amount, due);
      accrued = due - toInterest;
      body = Math.max(0, body - (f.amount - toInterest));
      if (accrued === 0) since = f.date;
      // Досрочное «каждый месяц» входит в платёж: часть сверх графика при
      // «уменьшить платёж» сокращает следующий.
      const over = Math.min(loan.extraMonthly ?? 0, f.amount - payment);
      if (loan.extraMode === 'payment' && over > 0) {
        const n = paymentsLeft(loan, body + over, payment, f.date);
        if (n) payment = Math.max(0, payment - paymentCut(over, rate, n, loan.schedule));
      }
    } else {
      const n = f.mode === 'payment' ? paymentsLeft(loan, body, payment, f.date) : null;
      body = Math.max(0, body - f.amount);
      if (n) payment = Math.max(0, payment - paymentCut(f.amount, rate, n, loan.schedule));
    }
  }
  if (body > 0) accrued += dayInterest(body, rate, cursor, today);
  const owed = roundHalfEven(accrued);
  return {
    principal: kop(roundHalfEven(body)),
    accrued: kop(owed),
    accruedSince: owed > 0 ? since : null,
    payment: kop(roundHalfEven(payment)),
    perDay: kop(roundHalfEven(dayInterest(body, rate, today, addDays(today, 1)))),
  };
}

/**
 * Основной долг на сегодня: названный на дату остаток минус то, что ушло в
 * тело после неё.
 */
export function loanBalance(loan: Loan, facts: readonly LoanFact[], today: PlainDate): Money {
  return loanState(loan, facts, today).principal;
}

/**
 * Долги на сегодня. Кредитка не трогается: её долг — счёт карты
 * (`withCardFacts`).
 *
 * Пересчитанный остаток получает дату «сегодня»: тогда тот же список, уехав
 * на другое устройство, не вычтет те же платежи второй раз. Набежавшие
 * проценты и нынешний платёж считаются при чтении и не хранятся.
 */
export function withLoanFacts(
  loans: readonly Loan[], factsByLoan: ReadonlyMap<string, readonly LoanFact[]>, today: PlainDate,
): Loan[] {
  return loans.map((l) => {
    const asOf = l.principalAsOf;
    if (l.kind === 'credit_card' || !asOf) return l;
    const facts = factsByLoan.get(l.id) ?? [];
    const st = loanState(l, facts, today);
    const moved = facts.some((f) => afterNamed(l, f) && f.date <= today);
    const out: Loan = { ...l, ...(moved ? { principal: st.principal, principalAsOf: today } : {}), payment: st.payment };
    if (st.accrued > ZERO && st.accruedSince) {
      out.accrued = st.accrued;
      out.accruedSince = st.accruedSince;
      out.accruedPerDay = st.perDay;
    } else if (st.perDay > ZERO) {
      out.accruedPerDay = st.perDay;
    }
    return out;
  });
}

// ─────────────────────────────────────────────────────── досрочное

export interface PrepayInput {
  principal: Money;
  ratePct: number;
  /** Платёж по графику. */
  payment: Money;
  /** Сколько сверх платежа, в день платежа. */
  extra: Money;
  /** Каждый месяц или один раз — в ближайший платёж. */
  monthly: boolean;
  /** Что сделает банк: уменьшит срок (платёж тот же) или платёж (срок тот же). */
  mode: 'term' | 'payment';
}

export interface PrepayResult {
  /** Процентов впереди без досрочного и с ним. */
  interestBefore: Money;
  interestAfter: Money;
  /** Сколько платежей осталось; `null` — платёж не покрывает проценты. */
  monthsBefore: number | null;
  monthsAfter: number | null;
  /** Проценты в следующем месяце. */
  nextInterestBefore: Money;
  nextInterestAfter: Money;
  /** Платёж после первого досрочного — при «уменьшит платёж». */
  paymentAfter: Money;
}

const MAX_MONTHS = 1200;

function simulate(p0: number, ratePct: number, payment: number, extra: number,
  monthly: boolean, mode: 'term' | 'payment', plan: number | null) {
  const r = ratePct / 1200;
  let p = p0;
  let pay = payment;
  let interest = 0;
  let n = 0;
  let nextInterest = 0;
  let payAfter = payment;
  let left = plan ?? Infinity;
  while (p > 0.5 && n < MAX_MONTHS) {
    const i = monthInterest(p, ratePct);
    if (i >= pay && extra <= 0) return { interest, months: null, nextInterest, payAfter };
    interest += i;
    p = p + i - Math.min(p + i, pay);
    n++;
    left--;
    if (extra > 0 && (monthly || n === 1)) p -= Math.min(p, extra);
    if (mode === 'payment' && p > 0.5 && left > 0 && r > 0) pay = roundHalfEven((p * r) / (1 - (1 + r) ** -left));
    else if (mode === 'payment' && p > 0.5 && left > 0) pay = roundHalfEven(p / left);
    if (n === 1) { nextInterest = monthInterest(p, ratePct); payAfter = pay; }
  }
  return { interest, months: n < MAX_MONTHS ? n : null, nextInterest, payAfter };
}

/**
 * Что даст досрочный платёж: на сколько меньше процентов, когда закроется
 * долг и сколько процентов в следующем месяце. Платёж сверх графика — в день
 * платежа: ровно так банк уменьшает тело.
 */
export function prepayEffect(input: PrepayInput): PrepayResult {
  const base = simulate(input.principal, input.ratePct, input.payment, 0, false, 'term', null);
  const out = simulate(input.principal, input.ratePct, input.payment, input.extra,
    input.monthly, input.mode, base.months);
  return {
    interestBefore: kop(base.interest),
    interestAfter: kop(out.interest),
    monthsBefore: base.months,
    monthsAfter: out.months,
    nextInterestBefore: kop(base.nextInterest),
    nextInterestAfter: kop(out.nextInterest),
    paymentAfter: kop(out.payAfter),
  };
}

export interface PrepayPlanInput {
  today: PlainDate;
  /** Основной долг на сегодня и проценты, набежавшие к сегодня. */
  principal: Money;
  accrued: Money;
  ratePct: number;
  /** Платёж по графику сейчас. */
  payment: Money;
  /** Ближайший платёж по графику (не раньше сегодня) и число месяца следующих. */
  nextPay: PlainDate;
  payDay: number;
  schedule?: Loan['schedule'];
  /** Сколько платежей осталось — нужно убывающему графику. */
  termMonths?: number;
  extra: Money;
  /** Когда внести разовый. Раньше сегодняшнего — считается как сегодня. */
  date: PlainDate;
  monthly: boolean;
  mode: PrepayMode;
}

export interface PrepayPlan {
  interestBefore: Money;
  interestAfter: Money;
  monthsBefore: number | null;
  monthsAfter: number | null;
  /** Последний платёж по графику без досрочного и с ним. */
  endBefore: PlainDate | null;
  endAfter: PlainDate | null;
  /** Сколько из ближайшего платежа уйдёт на проценты. */
  nextInterestBefore: Money;
  nextInterestAfter: Money;
  /** Платёж после досрочного — при «уменьшит платёж». */
  paymentAfter: Money;
}

/**
 * Досрочный по дням, как у банка: проценты копятся на остаток каждый день,
 * платёж по графику забирает набежавшие, досрочный уменьшает тело с того
 * дня, когда внесён. Поэтому дата важна: месяц делится на отрезок до взноса
 * и отрезок после.
 */
export function prepayPlan(input: PrepayPlanInput): PrepayPlan {
  const base = runPlan(input, false);
  const out = runPlan(input, true);
  return {
    interestBefore: kop(base.interest), interestAfter: kop(out.interest),
    monthsBefore: base.months, monthsAfter: out.months,
    endBefore: base.end, endAfter: out.end,
    nextInterestBefore: kop(base.nextInterest), nextInterestAfter: kop(out.nextInterest),
    paymentAfter: kop(roundHalfEven(out.payAfter)),
  };
}

function runPlan(input: PrepayPlanInput, withExtra: boolean) {
  const rate = input.ratePct;
  const diff = input.schedule === 'differentiated' && !!input.termMonths;
  let body: number = input.principal;
  let acc: number = input.accrued;
  let pay: number = input.payment;
  let left = diff ? input.termMonths! : annuityLeft(body, pay, rate);
  let part = diff ? body / input.termMonths! : 0;
  let cursor = input.today;
  let due = input.nextPay < input.today ? input.today : input.nextPay;
  const at = input.date < input.today ? input.today : input.date;
  const extra: number = withExtra ? input.extra : 0;
  let done = extra <= 0;
  let interest = 0;
  let nextInterest = 0;
  let payAfter = pay;
  let end: PlainDate | null = null;
  let n = 0;
  const put = () => {
    const x = Math.min(body, extra);
    const k = diff ? left : annuityLeft(body, pay, rate);
    body -= x;
    if (input.mode === 'payment' && k) {
      if (diff) part = body / k;
      else pay = Math.max(0, pay - paymentCut(x, rate, k));
    }
    payAfter = pay;
  };
  while (body > 0.5 && n < MAX_MONTHS) {
    if (!input.monthly && !done && at < due) {
      acc += dayInterest(body, rate, cursor, at);
      cursor = at;
      put();
      done = true;
    }
    acc += dayInterest(body, rate, cursor, due);
    cursor = due;
    const owed = roundHalfEven(acc);
    const amount = diff ? Math.min(body, part) + owed : Math.min(pay, body + owed);
    if (amount <= owed && body > 0.5) {
      return { interest, months: null, end: null, nextInterest: n === 0 ? owed : nextInterest, payAfter };
    }
    interest += owed;
    acc = 0;
    body -= amount - owed;
    n += 1;
    if (diff && left !== null) left -= 1;
    if (n === 1) nextInterest = owed;
    end = due;
    if (body > 0.5 && !done && (input.monthly || at <= due)) {
      put();
      if (!input.monthly) done = true;
    }
    due = withDayClamped(addMonths(due, 1), input.payDay);
  }
  return { interest, months: n < MAX_MONTHS ? n : null, end: n < MAX_MONTHS ? end : null, nextInterest, payAfter };
}

// ─────────────────────────────────────────────────────── очередь и слова

export interface DebtItem {
  id: string;
  name: string;
  principal: Money;
  ratePct: number;
  /** Платёж в месяц по графику; у кредитки — ноль. */
  payment: Money;
  /** Процентов в год сейчас: у кредитки, пока держится беспроцентный, — ноль. */
  cost: Money;
  /** Сколько платежей осталось по графику; `null` — не знаем. */
  monthsLeft: number | null;
  /** Кредитка: что внести и когда, чтобы беспроцентный период не сорвался. */
  card?: { missing: Money; paymentDate: PlainDate };
}

export type DebtMethod = 'rate' | 'size';

/**
 * Очередь гашения. «По ставке» — дорогие по ставке первыми, бесплатные
 * (беспроцентная карта, рассрочка) — в конце; «по сумме» — от маленького.
 */
export function debtQueue(items: readonly DebtItem[], method: DebtMethod): DebtItem[] {
  return [...items].sort((a, b) => {
    if (method === 'size') return a.principal - b.principal || b.ratePct - a.ratePct;
    const fa = a.cost > ZERO ? 1 : 0;
    const fb = b.cost > ZERO ? 1 : 0;
    return fb - fa || b.ratePct - a.ratePct || a.principal - b.principal;
  });
}

const pct = (r: number): string => `${(Math.round(r * 10) / 10).toString().replace('.', ',')}%`;
/** Сумма целыми рублями: в объяснении копейки только мешают читать. */
const rubles = (m: number): string => formatMoney(kop(Math.round(m / 100) * 100));
const per10k = (r: number): string => rubles(1_000_000 * r / 100);

/** «1 год 10 мес» — срок словами. */
export function monthsText(n: number): string {
  const y = Math.floor(n / 12);
  const m = n % 12;
  const yw = y % 10 === 1 && y % 100 !== 11 ? 'год'
    : y % 10 >= 2 && y % 10 <= 4 && (y % 100 < 12 || y % 100 > 14) ? 'года' : 'лет';
  return [y ? `${y} ${yw}` : '', m ? `${m} мес` : ''].filter(Boolean).join(' ') || 'меньше месяца';
}

/** Подпись под названием долга в очереди — одна короткая строка. */
export function debtReason(item: DebtItem, index: number, method: DebtMethod): string {
  if (item.card && item.cost === ZERO) return 'без процентов, пока беспроцентный держится';
  if (item.cost === ZERO) return 'без процентов';
  if (index > 0) return pct(item.ratePct);
  return method === 'size'
    ? `${pct(item.ratePct)} · самый маленький, закроется первым`
    : `${pct(item.ratePct)} · гасить выгоднее всего`;
}

/**
 * На сколько раньше закроется следующий долг, если платёж этого после его
 * закрытия отдавать туда. `null` — посчитать нечем.
 */
export function freedPaymentGain(item: DebtItem, next: DebtItem): number | null {
  if (item.monthsLeft === null || item.payment <= ZERO || next.payment <= ZERO) return null;
  const run = (boost: boolean): number | null => {
    let p: number = next.principal;
    let n = 0;
    while (p > 0.5 && n < MAX_MONTHS) {
      n++;
      const pay = next.payment + (boost && n > item.monthsLeft! ? item.payment : 0);
      const i = monthInterest(p, next.ratePct);
      if (i >= pay) return null;
      p = p + i - Math.min(p + i, pay);
    }
    return n < MAX_MONTHS ? n : null;
  };
  const before = run(false);
  const after = run(true);
  return before !== null && after !== null ? before - after : null;
}

/**
 * Почему долг на этом месте — для «i» у долга, на его цифрах.
 *
 * Главная путаница, которую это снимает: процентов в рублях больше у
 * крупного долга, и кажется, что гасить надо его. Но рубль досрочно снимает
 * больше там, где выше ставка.
 */
export function debtWhy(item: DebtItem, index: number, queue: readonly DebtItem[], method: DebtMethod): string {
  if (item.card) {
    if (item.cost > ZERO) {
      return `Беспроцентный период сорвался: ${pct(item.ratePct)} годовых. 10 000 ₽ досрочно сэкономят здесь ≈ ${per10k(item.ratePct)} в год.`;
    }
    return item.card.missing > ZERO
      ? `Пока беспроцентный период держится, процентов нет. Внесите ${rubles(item.card.missing)} до срока платежа — иначе ${item.ratePct > 0
        ? `${pct(item.ratePct)} годовых` : 'начнут начисляться проценты (ставку укажите в карточке карты)'}.`
      : 'Сумма по выписке внесена — процентов нет, досрочно гасить незачем.';
  }
  const prev = queue[index - 1];
  const next = queue[index + 1];
  const parts: string[] = [];
  if (item.cost === ZERO) {
    parts.push('Процентов нет — досрочно гасить незачем, платите по графику.');
  } else if (method === 'rate') {
    if (index === 0 || !prev || prev.cost === ZERO) {
      parts.push(`Самая высокая ставка — ${pct(item.ratePct)}. 10 000 ₽ досрочно сэкономят здесь ≈ ${per10k(item.ratePct)} в год.`);
    } else {
      parts.push(`Ставка ${pct(item.ratePct)} — ниже, чем у «${prev.name}» (${pct(prev.ratePct)}). 10 000 ₽ досрочно сэкономят здесь ≈ ${per10k(item.ratePct)} в год.`);
    }
  } else if (index === 0) {
    parts.push(`Самый маленький долг — ${rubles(item.principal)}. Закроется быстрее всех${item.payment > ZERO ? ` и освободит ${rubles(item.payment)} в месяц` : ''}.`);
  } else if (prev) {
    parts.push(`Остаток ${rubles(item.principal)} — больше, чем у «${prev.name}».`);
  }
  // Платёж, где проценты больше тела: как раз здесь кажется, что гасить надо
  // этот долг. Показываем, из чего платёж состоит, и что даёт досрочный.
  const interest = monthInterest(item.principal, item.ratePct);
  if (item.payment > ZERO && interest > 0 && interest * 2 >= item.payment) {
    parts.push(`Сейчас из платежа ${rubles(item.payment)} на проценты уходит ${rubles(interest)}, на сам долг — ${rubles(item.payment - interest)}. Внесёте больше — долг уменьшится, и процентов станет меньше.`);
  }
  if (next && item.cost > ZERO && item.monthsLeft !== null && item.monthsLeft > 0) {
    const gain = freedPaymentGain(item, next);
    parts.push(`Закроется через ${monthsText(item.monthsLeft)}.${gain !== null && gain >= 3
      ? ` Потом отдавайте его ${rubles(item.payment)} в «${next.name}» — тот закроется на ${monthsText(gain)} раньше.`
      : ''}`);
  }
  return parts.join(' ');
}

/** Общее правило — для «i» у заголовка «Долги». */
export const DEBTS_RULE = 'Проценты начисляются на остаток долга. Всё, что вносите сверх платежа, уменьшает сам долг — '
  + 'и со следующего месяца процентов меньше. Сильнее всего помогают регулярные пополнения. '
  + 'Нажмите на долг — посчитаю, сколько сэкономите.';

/** Пояснения к способам приоритета. */
export const METHOD_RULE: Record<DebtMethod, string> = {
  rate: 'Первым — долг с самой высокой ставкой: каждый рубль досрочно экономит там больше. '
    + 'Закрылся — его платёж отдаёте в следующий.',
  size: 'Первым — самый маленький долг: он закроется быстрее и освободит свой платёж. '
    + 'Экономит чуть меньше, зато результат виден раньше.',
};
